import type { ReactNode } from "react";
import { Eye } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { usePerfilVista } from "@/hooks/usePerfilVista";

/**
 * Aviso de "Ver como" (2026-10-27): prefijo "Solo lectura" pegado a la izquierda del selector
 * (un solo control, como un input con addon), visible de un vistazo; al pasar el mouse dice a
 * quién se ve, su rol y qué implica. Reemplaza la franja bajo el header (rompía el layout y salía
 * en capturas), el "?" (no se notaba) y el chip suelto. Solo pinta; la solo lectura la aplica
 * `lib/impersonation/solo-lectura`.
 */
export function AyudaVerComo({ detalle, className }: { detalle?: ReactNode; className?: string }) {
  const perfil = usePerfilVista();
  if (!perfil.impersonando) return null;
  return (
    <Tooltip delayDuration={100}>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={`Solo lectura: viendo como ${perfil.nombre}`}
          className={cn(
            // Prefijo del control "Ver como" (input group): mismo alto y borde primario, fondo
            // tenue; el selector va pegado a su derecha sin radio izquierdo.
            "inline-flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-l-md border border-r-0 border-primary bg-primary/10 px-2.5 text-[12px] font-medium text-primary-strong transition-colors hover:bg-primary/15 focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            className,
          )}
        >
          <Eye className="size-3.5" aria-hidden />
          <span className="hidden sm:inline">Solo lectura</span>
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom" align="start" className="max-w-[300px] space-y-1 text-xs">
        <p>
          Viendo como <span className="font-semibold">{perfil.nombre}</span>
          {perfil.rol ? ` (${perfil.rol})` : ""}: su menú y sus datos.
        </p>
        <p className="text-muted-foreground">
          Solo lectura: nada se guarda mientras ves como otra persona. Para hacer cambios, vuelve a tu
          vista con la X.
        </p>
        {detalle && <p className="text-muted-foreground">{detalle}</p>}
      </TooltipContent>
    </Tooltip>
  );
}
